// @ts-nocheck
import { 
  LayoutDashboard, 
  MessageSquare, 
  Lightbulb, 
  Layers, 
  Building2,
  Puzzle,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
  LogOut
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import productRadarLogo from '@/assets/product_radar_logo_png_blue.png';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { hasPermission, getRoleDisplayName, type UserRole } from '@product-radar/shared';
import { APP_VERSION } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';

interface SidebarProps {
  activeView: string;
  onNavigate: (view: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  userProfile?: any;
  onSignOut?: () => void;
  onSwitchWorkspace?: () => void;
  workspaceName?: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: any;
  requiredPermission?: string;
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, requiredPermission: 'canAccessDashboard' },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare, requiredPermission: 'canAccessFeedback' },
  { id: 'ideas', label: 'Ideas', icon: Lightbulb, requiredPermission: 'canAccessIdeas' },
  { id: 'clients', label: 'Client Management', icon: Building2, requiredPermission: 'canAccessClients' },
  { id: 'product-definition', label: 'Product Definition', icon: Layers, requiredPermission: 'canAccessProductDefinition' },
  { id: 'integrations', label: 'Integrations', icon: Puzzle, requiredPermission: 'canAccessIntegrations' },
  { id: 'settings', label: 'Settings', icon: Settings, requiredPermission: 'canAccessSettings' },
];

export function Sidebar({ activeView, onNavigate, isOpen, onToggle, userProfile, onSignOut, onSwitchWorkspace, workspaceName }: SidebarProps) {
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-300';
      case 'product_manager': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'contributor': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'product_manager': return 'Product Manager';
      case 'contributor': return 'Contributor';
      default: return 'Viewer';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const { workspace } = useWorkspace();

  // Check permissions for each item
  const userRole = userProfile?.role as UserRole;
  const roleDisplayName = getRoleDisplayName(userRole || 'viewer');

  return (
    <div className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 transition-all duration-300 z-50 shadow-sm ${isOpen ? 'w-64' : 'w-16'}`}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            {isOpen && (
              <div className="flex flex-col gap-2">
                {(() => {
                  const customLogo = workspace?.settings?.logo_url || workspace?.logo_url;
                  return (
                    <img
                      src={customLogo || productRadarLogo}
                      alt="Product Radar"
                      className={`w-auto object-contain ${customLogo ? 'h-32 max-h-32' : 'h-6 max-h-6'}`}
                    />
                  );
                })()}
                {workspaceName && (
                  <span className="text-xs text-gray-500">{workspaceName}</span>
                )}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.id;
                const hasAccess = !item.requiredPermission || hasPermission(userRole, item.requiredPermission as any);
                
                const buttonContent = (
                  <button
                    onClick={() => hasAccess && onNavigate(item.id)}
                    disabled={!hasAccess}
                    className={`w-full flex items-center rounded-lg transition-colors duration-200 ${
                      !hasAccess
                        ? 'text-gray-400 cursor-not-allowed opacity-50'
                        : isActive 
                          ? 'bg-gray-100 text-gray-900' 
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } ${isOpen ? 'px-2.5 py-1.5' : 'px-2 py-1.5 justify-center'}`}
                  >
                    <Icon className={`h-4 w-4 flex-shrink-0 ${isActive && hasAccess ? 'text-primary' : ''}`} />
                    {isOpen && (
                      <>
                        <span className="ml-3 text-sm font-medium truncate">{item.label}</span>
                      </>
                    )}
                  </button>
                );

                return (
                  <li key={item.id}>
                    {buttonContent}
                  </li>
                );
              })}
            </ul>
        </nav>

        {/* User Profile */}
        <div className="border-t border-gray-100">
          <div className="p-3">
            {isOpen ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center space-x-3 hover:bg-gray-50 rounded-lg p-2 transition-colors">
                    <div className="w-8 h-8 bg-[#245F73] rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {userProfile ? getInitials(userProfile.full_name) : 'U'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {userProfile?.full_name || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {userProfile?.email || ''}
                      </p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{userProfile?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{userProfile?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled>
                    <Badge className={getRoleColor(userProfile?.role || 'viewer')}>
                      {getRoleLabel(userProfile?.role || 'viewer')}
                    </Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onSwitchWorkspace}>
                    <Building2 className="mr-2 h-4 w-4" />
                    Switch Workspace
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex justify-center">
                <div className="w-8 h-8 bg-[#245F73] rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {userProfile ? getInitials(userProfile.full_name) : 'U'}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Version Info */}
          {isOpen && (
            <div className="px-4 pb-3 border-t border-gray-100 pt-3">
              <p className="text-[10px] text-gray-400 text-center">
                v{APP_VERSION}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}